import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import { faMobile } from '@fortawesome/pro-regular-svg-icons/faMobile';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { DefaultListParameters } from 'assets/appConfig';
import { GlobalClassKeys, TextColorStyleClasses } from 'assets/jss/common-styles';
import AdvFilter, { AdvFilterCommand, AdvFilterComponent, AdvFilterPicker, AdvFilterResult } from 'components/AdvFilter';
import { CmdFromDate, CmdQueueCloudOnly, CmdQueueDone, CmdQueueFailed, CmdQueueMailOnly, CmdQueuePending, CmdToDate, CmdUser, CommandLabels } from 'components/AdvFilter/CommonCommands';
import ButtonResetFilter from 'components/Buttons/ButtonResetFilter';
import { LoadingIndicator } from 'components/CallServer/LoadingIndicator';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import { MuiFilterAdvancedContainer, MuiFilterAppBar, MuiFilterToolbar } from 'components/MuiExtras/Filter';
import MuiMessageBox from 'components/MuiExtras/MessageBox/MuiMessageBox';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiPagination from 'components/MuiExtras/MuiPagination';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import { RefreshLink } from 'components/RefreshLink';
import { withSnackbar, WithSnackbar } from 'components/SnackbarProvider/Snackbar';
import QueueViewStatusName from 'components/Translations/QueueViewStatusName';
import { Formaters } from 'lib/Formarters';
import { QueueEventType, QueueViewStatus } from 'models/ApiEnums';
import { PagedResult, QueueEventViewModel, QueueFilter } from 'models/ApiModels';
import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    advFilters: AdvFilterResult[];
    searchText: string;
    orderByMenuAnchorEl: any;
    dialog: any;
    list: PagedResult<QueueEventViewModel>;
    offset: number;
    loading: boolean;
    lastError: any;
}

interface IProps {
}

interface IRouteProps {
    id: string;
}

type Props = IProps & RouteComponentProps<IRouteProps> & WithSnackbar & WithTranslation & WithStyles<typeof TextColorStyleClasses>;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class QueueEventList extends React.Component<Props, IState> {
    private advFilterRef: AdvFilterComponent;
    private filter: QueueFilter;

    constructor(props: Props) {
        super(props);
        this.state = {
            advFilters: [],
            searchText: "",
            list: { items: null, total: 0 },
            offset: 0,
            orderByMenuAnchorEl: null,
            loading: false,
            lastError: null,
            dialog: null,
        };

        this.resetFilter();
    }

    ///
    private res(r: string) {
        return this.props.t("Views.System.QueueList." + r);
    }

    private loadData(offset: number) {

        this.setState({ loading: true, orderByMenuAnchorEl: null, dialog: null });

        api.getQueueEvents({ filter: this.filter, range: { offset, take: DefaultListParameters.viewRecordsByPage } })
            .then((result) => {
                this.setState({ list: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ list: null, lastError: error, loading: false, offset: 0 });
            });
    }

    ///
    private isFiltered() {
        return this.filter.status != null || this.filter.subject != null || this.filter.userId != null;
    }

    ///
    private resetFilter() {
        this.filter = {
            subject: null,
            status: null,
            userId: null
        };
    }

    ///
    private getCommands(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdQueueMailOnly, displayName: cmdLabels[CmdQueueMailOnly], group: 0, canRepeat: false, optionGroup: "queue" },
            { keyword: CmdQueueCloudOnly, displayName: cmdLabels[CmdQueueCloudOnly], group: 0, canRepeat: false, optionGroup: "queue" },

            { keyword: CmdQueueDone, displayName: cmdLabels[CmdQueueDone], group: 1, canRepeat: false },
            { keyword: CmdQueueFailed, displayName: cmdLabels[CmdQueueFailed], group: 1, canRepeat: false },
            { keyword: CmdQueuePending, displayName: cmdLabels[CmdQueuePending], group: 1, canRepeat: false },

            { keyword: CmdUser, displayName: cmdLabels[CmdUser], picker: AdvFilterPicker.User, group: 2, canRepeat: false, optionGroup: "member" },

            { keyword: CmdFromDate, displayName: cmdLabels[CmdFromDate], picker: AdvFilterPicker.Calendar, group: 4 },
            { keyword: CmdToDate, displayName: cmdLabels[CmdToDate], picker: AdvFilterPicker.Calendar, group: 4 },
        ];
    }

    ///
    private handleUpdateFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        if (searchText.length == 0 && advFilters.length == 0) {
            this.resetFilter();
        }
        else {
            this.filter.subject = searchText.length == 0 ? null : searchText;
            this.filter.userId = advFilters.find(x => x.keyword == CmdUser)?.value;
            this.filter.startDate = advFilters.find(x => x.keyword == CmdFromDate)?.value;
            this.filter.endDate = advFilters.find(x => x.keyword == CmdToDate)?.value;

            this.filter.status = QueueViewStatus.Unset;
            if (advFilters.findIndex(x => x.keyword == CmdQueueDone) > -1) this.filter.status |= QueueViewStatus.Done;
            if (advFilters.findIndex(x => x.keyword == CmdQueueFailed) > -1) this.filter.status |= QueueViewStatus.Failed;
            if (advFilters.findIndex(x => x.keyword == CmdQueuePending) > -1) this.filter.status |= QueueViewStatus.Pending;
            if (advFilters.findIndex(x => x.keyword == CmdQueuePending) > -1) this.filter.status |= QueueViewStatus.Pending;

            this.filter.queueEventType = QueueEventType.Unset;
            if (advFilters.findIndex(x => x.keyword == CmdQueueMailOnly) > -1) this.filter.queueEventType |= QueueEventType.Mail;
            if (advFilters.findIndex(x => x.keyword == CmdQueueCloudOnly) > -1) this.filter.queueEventType |= QueueEventType.CloudNotification;
        }

        this.setState({ advFilters });

        this.loadData(0);
    };

    ///
    private renderAdvFilter(): JSX.Element {
        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >

                    <MuiFilterAdvancedContainer>

                        <AdvFilter
                            id="advfilter"
                            commands={this.getCommands()}
                            innerRef={(ref) => this.advFilterRef = ref}
                            selectedCommands={this.state.advFilters}
                            placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                            placeHolderFocus={this.props.t("Common.Filter.Adv.InputPlaceholderFocus")}
                            title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                            onUpdateSelection={this.handleUpdateFilter}
                        />

                    </MuiFilterAdvancedContainer>

                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    //
    private renderItems() {
        const { list } = this.state;

        return (
            <div>
                <MuiContainer margin="dense">
                    <div className={GlobalClassKeys.textRight}>
                        <RefreshLink onClick={() => this.loadData(0)} />
                    </div>
                </MuiContainer>

                <MuiPaper>
                    <MuiContainer margin="dense" >
                        <TableContainer >
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>&nbsp;</TableCell>
                                        <TableCell>{this.res("PostedOnLabel")}</TableCell>
                                        <TableCell>{this.res("RecipientLabel")}</TableCell>
                                        <TableCell>{this.res("SubjectLabel")}</TableCell>
                                        <TableCell>{this.res("LastProcessingLabel")}</TableCell>
                                        <TableCell>{this.res("LastResultLabel")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        list.items.map((item, index) => {
                                            var cls = item.status == QueueViewStatus.Failed
                                                ? this.props.classes.error
                                                : item.status == QueueViewStatus.Pending
                                                    ? this.props.classes.warning
                                                    : null;
                                            return (
                                                <TableRow key={index} >
                                                    <TableCell className={cls}>
                                                        <FontAwesomeIcon
                                                            icon={item.queueEventType == QueueEventType.Mail ? faEnvelope : faMobile}
                                                            title={this.res(item.queueEventType == QueueEventType.Mail ? "MailTitle" : "CloudTitle")}
                                                        />
                                                    </TableCell>
                                                    <TableCell className={cls}>{Formaters.localeDate(item.postedOn)}<br />{Formaters.localeTime(item.postedOn)}</TableCell>
                                                    <TableCell className={cls}>{item.recipientName}<br /><small>{item.recipientEmail}</small></TableCell>
                                                    <TableCell className={cls}>{item.subject}</TableCell>
                                                    <TableCell className={cls}>{Formaters.localeDate(item.lastProcessingDate)}<br />{Formaters.localeTime(item.lastProcessingDate)}</TableCell>
                                                    <TableCell className={cls}>
                                                        <div
                                                            title={item.status == QueueViewStatus.Failed ? `(${item.lastStatusCode}) ${item.lastStatus}` : null}
                                                            className={item.status == QueueViewStatus.Failed ? GlobalClassKeys.clickable : null}
                                                        >
                                                            <QueueViewStatusName status={item.status} />
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </MuiContainer>
                    <MuiPagination
                        limit={DefaultListParameters.viewRecordsByPage}
                        offset={this.state.offset}
                        total={this.state.list.total}
                        onClick={(e, offset) => this.loadData(offset)}
                    />
                </MuiPaper >
            </div>
        );
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public render() {

        const { dialog, list, lastError, loading } = this.state;

        return (
            <div>
                {dialog}

                {
                    (!lastError && (loading || list.total > 0 || this.isFiltered())) &&
                    <MuiContainer margin="normal">
                        {this.renderAdvFilter()}
                    </MuiContainer>
                }

                {
                    lastError
                        ? <RequestErrorReport error={lastError} />
                        : loading
                            ? <LoadingIndicator />
                            : list.total == 0 && !this.isFiltered()
                                ? <MuiMessageBox variant="info" message={this.res("RsResultText_zero")} />
                                : list.total == 0 && this.isFiltered()
                                    ? (
                                        <MuiMessageBox
                                            variant="warning"
                                            action={<ButtonResetFilter color="warning" onClick={() => this.advFilterRef.resetFilter()} />}
                                            message={this.res("RsFilteredResultText_zero")}
                                        />
                                    )
                                    : this.renderItems()
                }

            </div>
        );
    }
}

export default withRouter(withSnackbar(withTranslation()(withStyles(TextColorStyleClasses)(QueueEventList))));